import { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  Pagination
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import { authService } from '../../services/authService';
import Avatar from '@mui/material/Avatar';

function Pilotes() {
  const [pilotes, setPilotes] = useState([]);
  const [filteredPilotes, setFilteredPilotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const pilotesParPage = 9;
  const navigate = useNavigate();
  const location = useLocation();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const fetchPilotes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/users?role=pilote`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la récupération des pilotes');
        }

        const data = await response.json();
        setPilotes(data.users);
        setFilteredPilotes(data.users);
      } catch (err) {
        console.error("Erreur lors du chargement des pilotes:", err);
        setError("Impossible de charger la liste des pilotes");
      } finally {
        setLoading(false);
      }
    };

    fetchPilotes();
  }, []);

  useEffect(() => {
    const filtered = pilotes.filter(pilote => 
      pilote.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pilote.prenom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pilote.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredPilotes(filtered);
  }, [searchQuery, pilotes]);

  useEffect(() => {
    // Récupérer le numéro de page depuis l'URL
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page')) || 1;
    setPage(pageParam);
  }, [location.search]);

  // Pagination
  const indexDernierElement = page * pilotesParPage;
  const indexPremierElement = indexDernierElement - pilotesParPage;
  const pilotesAffiches = filteredPilotes.slice(indexPremierElement, indexDernierElement);
  const nombrePages = Math.ceil(filteredPilotes.length / pilotesParPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Mettre à jour l'URL
    navigate(`/pilotes?page=${newPage}`);
    window.scrollTo({
      top: document.getElementById('pilotes-list').offsetTop - 20,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        sx={{ 
          mb: 3,
          opacity: 0,
          animation: 'fadeIn 0.8s ease-out forwards',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}
      >
        Gestion des Pilotes
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        gap: 2,
        mb: 4,
        opacity: 0,
        animation: 'fadeIn 0.8s ease-out 0.3s forwards',
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 }
        }
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher un pilote par nom, prénom ou email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            bgcolor: 'background.paper',
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/pilotes/new')}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Ajouter un pilote
        </Button>
      </Box>

      <Box id="pilotes-list">
        <Grid container spacing={4}>
          {pilotesAffiches.map((pilote, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              key={pilote.id_users}
              sx={{ 
                opacity: 0,
                animation: `fadeIn 0.8s ease-out ${0.2 + index * 0.1}s forwards`,
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                }
              }}
            >
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    cursor: 'pointer'
                  }
                }}
              >
                <IconButton
                  className="edit-button"
                  onClick={() => navigate(`/pilotes/${pilote.id_users}/edit`)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    '&:hover': {
                      bgcolor: 'primary.main'
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>

                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={pilote.photoURL}
                      sx={{ 
                        width: 56, 
                        height: 56, 
                        mr: 2,
                        bgcolor: 'primary.main'
                      }}
                    >
                      {pilote.prenom?.[0]}{pilote.nom?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" component="h2">
                        {pilote.nom} {pilote.prenom}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ color: 'primary.main', mr: 1, fontSize: '1rem' }} />
                        <Typography variant="body2" color="text.secondary">
                          {pilote.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination avec animation basée sur le temps aussi */}
        {nombrePages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              mb: 2,
              opacity: 0,
              animation: 'fadeIn 0.8s ease-out 1.5s forwards',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(20px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            <Pagination 
              count={nombrePages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Box>

      {/* Message si aucun pilote */}
      {filteredPilotes.length === 0 && (
        <Box 
          sx={{ 
            textAlign: 'center', 
            mt: 4,
            opacity: scrollY > 100 ? 1 : 0,
            transform: `translateY(${scrollY > 100 ? 0 : 30}px)`,
            transition: 'all 0.6s ease-out',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {searchQuery ? "Aucun pilote ne correspond à votre recherche" : "Aucun pilote enregistré"}
          </Typography>
        </Box>
      )}
    </Container>
  );
}

export default Pilotes; 